import React from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@component/dialog";
import { EyeIcon } from "lucide-react";
import PreviewCompanyHeader from "./preview-company-header";
import PreviewInterviewCardHeader from "./preview-interview-card-header";
import PreviewVideoSection from "./preview-video-section";
import PreviewInterviewSidebar from "./preview-interview-sidebar";
import PreviewProgressBar from "./preview-progress-bar";

interface PreviewWaitingRoomProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    companyLogo?: string;
    aiBotLogo?: string;
    accentColor?: string;
    companyName?: string;
    showBranding?: boolean;
    videoSrc?: string;
}

export default function PreviewWaitingRoom({
    open,
    onOpenChange,
    companyLogo,
    accentColor = "#2563eb",
    companyName = "Your Company",
    showBranding = true,
    videoSrc,
}: PreviewWaitingRoomProps) {
    const [timeRemaining, setTimeRemaining] = React.useState("01:29");

    // Simulate countdown timer
    React.useEffect(() => {
        if (!open) return;

        const interval = setInterval(() => {
            setTimeRemaining((prev) => {
                const [minutes, seconds] = prev.split(":").map(Number);
                const totalSeconds = minutes * 60 + seconds;

                if (totalSeconds <= 1) {
                    return "00:00";
                }

                const newTotal = totalSeconds - 1;
                const newMinutes = Math.floor(newTotal / 60);
                const newSeconds = newTotal % 60;

                return `${newMinutes.toString().padStart(2, "0")}:${newSeconds.toString().padStart(2, "0")}`;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [open]);

    // Reset timer when dialog opens
    React.useEffect(() => {
        if (open) {
            setTimeRemaining("01:29");
        }
    }, [open]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-7xl w-full h-[90vh] p-0 overflow-hidden">
                <DialogHeader className="p-6 pb-0">
                    <DialogTitle className="text-lg font-semibold flex items-center text-blue-600">
                        <EyeIcon className="w-5 h-5 mr-2" />
                        Preview as Candidate
                    </DialogTitle>
                    <p className="text-sm text-gray-600 mt-1">
                        This is a preview of how candidates will see your branding in the AI interview waiting room.
                    </p>
                </DialogHeader>

                {/* Preview Container */}
                <div className="flex-1 p-0 pt-0">
                    <div className="pt-0 pb-6 px-0 h-full flex flex-col">
                        <PreviewCompanyHeader
                            companyLogo={showBranding ? companyLogo : `${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/ourteam_logo_wordmark.png`}
                            companyName={companyName}
                        />

                        {/* Interview Card */}
                        <div className="max-w-6xl mx-auto w-full bg-white overflow-hidden mt-4">
                            <PreviewInterviewCardHeader />

                            {/* Interview Content */}
                            <div className="flex gap-8">
                                <PreviewVideoSection videoSrc={videoSrc} />

                                <PreviewInterviewSidebar
                                    accentColor={accentColor}
                                    showBranding={showBranding}
                                    timeRemaining={timeRemaining}
                                />
                            </div>
                        </div>

                        <PreviewProgressBar
                            accentColor={accentColor}
                            showBranding={showBranding}
                        />
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
